// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/MyCustomQuotes/CreateGroupModal";
import addToQuote from "@bg/App/Handlers/CustomQuote/AddNewCustomQuoteLine";
import getQuoteDetails from "@bg/App/Handlers/CustomQuote/GetCustomQuote";
import setSelectedLines from "@bg/App/Handlers/CustomQuote/SetSelectedLines";
import updateLinePrice from "@bg/App/Handlers/CustomQuote/UpdateLinePrice";
import updateSortBy from "@bg/App/Handlers/CustomQuote/UpdateSortBy";
import { CustomQuoteModel } from "@bg/App/Services/CustomQuoteService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { printItems } from "@bg/App/Utilities/PrintItemsQrCode";
import { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import OrderUpload, { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal, {
    OrderUploadErrorsModalStyles,
} from "@insite/content-library/Components/OrderUploadErrorsModal";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import ProductSelectorVariantModal from "@insite/content-library/Components/ProductSelectorVariantModal";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote?: CustomQuoteModel;
    widgetId: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    selectedLineIds: state.myCustomQuoteDetails.selectedLineIds,
    productInfos: state.myCustomQuoteDetails.productsInfo,
    sortBy: state.myCustomQuoteDetails.sortBy,
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
    quoteState: state.myCustomQuoteDetails,
    myCustomQuotesUrl: getPageLinkByPageType(state, "MyCustomQuotesPage")?.url,
});

const mapDispatchToProps = {
    setSelectedLines,
    updateLinePrice,
    updateSortBy,
    addToQuote,
    getQuoteDetails,
};

type Props = OwnProps & HasHistory & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface RequestQuoteOptionsStyle {
    container?: InjectableCss;
    actionsWrapper?: InjectableCss;
    addItems?: ButtonPresentationProps;
    middleSection?: InjectableCss;
    selectAllCheckbox?: CheckboxPresentationProps;
    sortBySelect?: SelectPresentationProps;
    addItemsGridContainer?: InjectableCss;
    productSelectorGridItem?: InjectableCss;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsGridItem?: InjectableCss;
    uploadItemsLink?: LinkPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
    closeAddItemsToList?: IconProps;
    calculateMarginButton?: ButtonPresentationProps;
    printAllCodes?: ButtonPresentationProps;
    hiddenOverflow?: InjectableCss;
}

export const styles: RequestQuoteOptionsStyle = {
    container: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            position: relative;
            @media (max-width: 1000px) {
                flex-wrap: wrap;
            }
            @media (max-width: 481px) {
                position: relative;
            }
        `,
    },
    middleSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            @media (max-width: 1024px) {
                justify-content: flex-start;
            }
            @media (max-width: 481px) {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    selectAllCheckbox: {
        mergeCss: true,
        css: css`
            margin-right: 30px;
            @media (max-width: 481px) {
                margin-right: 0;
                margin-bottom: 25px;
                label {
                    font-weight: ${fontConfig.weight.semiBold};
                }
            }
        `,
    },
    addItems: {
        css: css`
            padding: 0 10px;

            && ${IconWrapper} {
                left: -2px;
            }
            && svg {
                transition: all 200ms ease-in-out;
                color: ${getColor("text")};
            }
            &&:hover {
                svg {
                    color: ${getColor("common.white")};
                }
            }
        `,
        icon: {
            src: "AddToList",
            position: "left",
        },
    },
    printAllCodes: {
        typographyProps: {
            lineHeight: "20px",
        },
        css: css`
            margin-left: 30px;
            padding: 0 10px;
            && ${IconWrapper} {
                height: 18px;
                width: 18px;
                top: 3px;
                left: -4px;
            }
            && svg {
                transition: all 200ms ease-in-out;
                height: 18px;
                width: 18px;
                color: ${getColor("text")};
            }
            &&:hover {
                svg {
                    color: ${getColor("common.white")};
                }
            }
        `,
        icon: {
            src: "QrCode",
            position: "left",
        },
    },
    sortBySelect: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                width: auto !important;
                align-items: center;
                label {
                    width: auto;
                    margin-right: 8px;
                    font-weight: ${fontConfig.weight.semiBold};
                }
                @media (max-width: 481px) {
                    width: 100% !important;
                }
            `,
            formInputWrapper: css`
                width: 175px;
                @media (max-width: 481px) {
                    flex-grow: 1;
                }
            `,
            inputSelect: css`
                height: 32px;
                font-size: ${fontConfig.fontSize.small}px;
                font-weight: ${fontConfig.weight.semiBold};
            `,
        },
        iconProps: {
            css: css`
                display: inline;
                top: 0 !important;
            `,
        },
    },
    productSelectorGridItem: {
        css: css`
            display: flex;
            @media (max-width: 680px) {
                flex-wrap: wrap;
            }
        `,
    },
    productSelectorStyles: {
        container: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                padding: 0;
                width: 100%;
                align-items: flex-start;
            `,
        },
        searchWrapper: {
            css: css`
                max-width: 450px;
                width: 100%;
                margin-right: 30px;
            `,
        },
        searchDynamicDropdown: {
            cssOverrides: {
                formField: css`
                    flex-direction: row;
                    align-items: center;
                    width: 100%;

                    label {
                        width: auto;
                        font-weight: ${fontConfig.weight.bold};
                        font-size: ${fontConfig.fontSize.small}px;
                        margin-right: 8px;
                        white-space: nowrap;
                    }
                    @media (max-width: 650px) {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                `,
                formInputWrapper: css`
                    margin-top: 0 !important;
                `,
                inputSelect: css`
                    font-size: ${fontConfig.fontSize.small}px;
                    height: 32px;
                `,
                selectedText: css`
                    padding: 5px 10px;
                `,
                list: css`
                    top: 32px;
                    width: 100%;
                    border-top: 0;
                `,
            },
            iconProps: {
                css: css`
                    &&&&&& {
                        right: 0;
                        top: 0;
                    }
                `,
            },
        },
        qtyWrapper: {
            css: css`
                display: flex;
                align-items: center;
                width: 160px;
                padding: 0;
                margin-right: 30px;

                &&& > label {
                    width: auto;
                    font-weight: ${fontConfig.weight.bold};
                    font-size: ${fontConfig.fontSize.small}px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                label + div {
                    margin-top: 0;
                    height: 32px;
                }

                &&& input {
                    height: 32px !important;
                }
            `,
        },
        buttonWrapper: {
            css: css`
                margin-top: 0 !important;
                @media (max-width: 481px) {
                    margin-top: 10px !important;
                }
            `,
        },
        selectButton: {
            css: css`
                min-height: 32px !important;
            `,
        },
    } as ProductSelectorStyles,
    uploadItemsLink: {
        css: css`
            margin: 0;
            white-space: nowrap;
            margin-right: 15px;
            height: 32px;
        `,
    },
    closeAddItemsToList: {
        src: "X",
        css: css`
            margin: 0;
            margin-top: 5px;
            cursor: pointer;
            align-items: flex-start;
        `,
    },
    calculateMarginButton: {
        css: css`
            padding: 0 10px;
            && ${IconWrapper} {
                width: 18px;
                top: 5px;
                left: -3px;
            }
            && svg {
                transition: all 200ms ease-in-out;
                height: 18px;
                width: 18px;
                color: ${getColor("text")};
            }
            &&:hover {
                svg {
                    color: ${getColor("common.white")};
                }
            }
        `,
        icon: {
            src: "Calculator",
            position: "left",
        },
    },
    hiddenOverflow: {
        css: css`
            @media (max-width: 481px) {
                position: absolute;
                top: -10px;
                right: 0;
                nav,
                nav > button:first-child {
                    height: 34px;
                    min-height: 34px;
                    width: 34px;
                }
            }
        `,
    },
};

const MyCustomQuoteDetailsOptions = ({
    quote,
    widgetId,
    setSelectedLines,
    selectedLineIds,
    productInfos,
    updateLinePrice,
    updateSortBy,
    sortBy,
    addToQuote,
    uncategorizedGroupName,
    quoteState,
    getQuoteDetails,
    myCustomQuotesUrl,
    history,
}: Props) => {
    if (!quote) {
        return null;
    }
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const [groupingModalIsOpen, setGroupingModalIsOpen] = useState(false);
    const [productInfo, setProductInfo] = useState<ProductInfo>({} as ProductInfo);
    const [product, setProduct] = useState<ProductModel>({} as ProductModel);
    const [creatingNewGroup, setIsCreatingNewGroup] = useState(false);
    const [errorMsg, setError] = useState("");
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [calculateMarginModalIsOpen, setCalculateMarginModalIsOpen] = useState(false);
    const [marginPercent, setMarginPercent] = useState("");
    const [isSorting, setIsSorting] = useState(false);

    useEffect(() => {
        if (quote && selectedLineIds && Object.keys(selectedLineIds)?.length > 0) {
            const selected: boolean[] = [];
            Object.keys(quote.groupLinesModel).forEach((key: string) => {
                selected.push(
                    selectedLineIds[key] ? selectedLineIds[key].length === quote.groupLinesModel[key].length : false,
                );
            });
            setIsAllSelected(selected.every(i => i));
        } else {
            setIsAllSelected(false);
        }
    }, [selectedLineIds]);

    useEffect(() => {
        if (quoteState.quote && !quoteState.isLoading && !quoteState.errorLoading) {
            getQuoteDetails({
                quoteId: quoteState.quote.id,
                sortBy,
                onError: (result: any) => {
                    setIsSorting(false);
                    if (myCustomQuotesUrl) {
                        history.push(myCustomQuotesUrl);
                    }
                },
                onComplete: () => {
                    setIsSorting(false);
                },
                onSuccess: () => {
                    setIsSorting(false);
                },
            });
        }
    }, [sortBy]);

    const selectProductHandler = (productInfo: ProductInfo, product: ProductModel) => {
        let isAlreadyRelatedProduct = false;
        Object.keys(quote.groupLinesModel).forEach(group => {
            quote.groupLinesModel[group].forEach(line => {
                if (line.relatedItems) {
                    line.relatedItems.forEach(related => {
                        if (related.productResult.id === product.id) {
                            isAlreadyRelatedProduct = true;
                        }
                    });
                }
            });
        });

        if (isAlreadyRelatedProduct) {
            setError("Item is already added as a related product");
            return;
        }
        setError("");
        setGroupingModalIsOpen(true);
        setProduct(product);
        setProductInfo(productInfo);
    };

    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };

    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };

    const uploadProductsHandler = (products: ProductDto[]) => {
        const linesModel = products.map(o => ({
            productErpNumber: o.erpNumber,
            qty: o.qtyOrdered,
        }));
        addToQuote({
            customQuoteId: quote.id,
            groupName: uncategorizedGroupName.toLowerCase(),
            linesModel,
            onComplete(res: any) {
                if (res.successful) {
                    toasterContext.addToast({ body: translate("Items Added"), messageType: "success" });
                }
            },
        });
        setUploadItemsModalIsOpen(false);
    };

    const closeModalHandler = () => {
        setGroupingModalIsOpen(false);
        setIsCreatingNewGroup(false);
    };

    const callAfterSuccess = (selectedGroup: string) => {
        dispatch({
            type: "Pages/xProductListDetails/TemporarilySaveEmptyList",
            emptyList: selectedGroup,
        });
    };

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setSelectedLines({ selectAll: value, clearAll: !value });
    };

    const printQrCodes = () => {
        const lines: ProductModel[] = [];
        const lineInfos: ProductInfo[] = [];
        Object.keys(quote.groupLinesModel).forEach(group => {
            quote.groupLinesModel[group].forEach(line => {
                lines.push(line.productResult);
                lineInfos.push(productInfos?.[line.lineId]);

                if (line.relatedItems) {
                    line.relatedItems.forEach(related => {
                        lines.push(related.productResult);
                        lineInfos.push(productInfos?.[related.lineId]);
                    });
                }
            });
        });
        printItems(`.canvas-wrapper canvas`, lines, lineInfos, false, true);
    };

    const handleMarginChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!/^[0-9\b]+$/.test(event.target.value) && event.target.value !== "") {
            return;
        }
        setMarginPercent(event.target.value);
    };

    const handleMarginBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const price = parseInt(event.target.value, 10);
        if (Number.isNaN(price)) {
            setMarginPercent("0");
            return;
        }
        setMarginPercent(price.toString());
    };

    const handleCloseCalculateMarginModal = () => {
        setCalculateMarginModalIsOpen(false);
    };

    const applyMargin = () => {
        if (!productInfos) {
            return;
        }
        setCalculateMarginModalIsOpen(false);
        const margin = parseFloat(marginPercent) / 100;

        const linesModel = Object.values(quote.groupLinesModel || {}).reduce(
            (acc: { lineId: string; price: number }[], groupLines) => {
                const groupLinesModel = groupLines.reduce((innerAcc, line) => {
                    const price = line.extendedPrice
                        ? Number(line.extendedPrice)
                        : productInfos[line.lineId]?.pricing?.unitNetPrice;

                    innerAcc.push({ lineId: line.lineId, price });

                    if (line.relatedItems) {
                        const relatedItemsModels = line.relatedItems.map(related => ({
                            lineId: related.lineId,
                            price: related.extendedPrice
                                ? Number(related.extendedPrice)
                                : productInfos[related.lineId]?.pricing?.unitNetPrice,
                        }));
                        return innerAcc.concat(relatedItemsModels);
                    }

                    return innerAcc;
                }, [] as { lineId: string; price: number }[]);

                return acc.concat(groupLinesModel); // Concatenate group lines
            },
            [],
        );

        updateLinePrice({
            customQuoteId: quote.id,
            updatePrice: true,
            linesModel,
            updateMargin: true,
            margin,
        });
    };

    const handleSelectSortOptionChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const sortBy = event.currentTarget.value as "" | "name asc" | "name desc";
        setIsSorting(true);
        updateSortBy({ sortBy });
    };

    return (
        <StyledWrapper {...styles.container}>
            {!addItemsToListIsOpen && (
                <StyledWrapper {...styles.actionsWrapper}>
                    <Hidden below="lg">
                        <Button sizeVariant="small" {...styles.addItems} onClick={() => setAddItemsToListIsOpen(true)}>
                            {translate("Add Item To Quote")}
                        </Button>
                    </Hidden>

                    <StyledWrapper {...styles.middleSection}>
                        <Checkbox
                            checked={isAllSelected}
                            onChange={selectAllChangeHandler}
                            data-test-selector="selectAllGroupItems"
                            {...styles.selectAllCheckbox}
                        >
                            {translate("Select All Products")}
                        </Checkbox>
                        <Select
                            {...styles.sortBySelect}
                            onChange={handleSelectSortOptionChange}
                            labelPosition="left"
                            label={translate("Sort By")}
                            value={sortBy}
                        >
                            <option value="">{translate("Choose Sort Order")}</option>
                            <option value="name asc">Product: A-Z</option>
                            <option value="name desc">Product: Z-A</option>
                        </Select>
                        {isSorting && (
                            <LoadingSpinner
                                css={css`
                                    margin-left: 8px;
                                    margin-right: 8px;
                                `}
                                size={26}
                                color="secondary"
                            />
                        )}
                    </StyledWrapper>

                    <Hidden below="lg">
                        <Button
                            sizeVariant="small"
                            onClick={() => {
                                setMarginPercent("");
                                setCalculateMarginModalIsOpen(true);
                            }}
                            {...styles.calculateMarginButton}
                        >
                            {translate("Calculate All Margins")}
                        </Button>
                        <Button onClick={printQrCodes} sizeVariant="small" {...styles.printAllCodes}>
                            {translate("Print All Qr Codes")}
                        </Button>
                    </Hidden>
                    <Hidden above="md" {...styles.hiddenOverflow}>
                        <OverflowMenu>
                            <Clickable onClick={() => setAddItemsToListIsOpen(true)}>
                                {translate("Add Item To Quote")}
                            </Clickable>
                            <Clickable onClick={() => setCalculateMarginModalIsOpen(true)}>
                                {translate("Calculate All Margins")}
                            </Clickable>
                            <Clickable onClick={printQrCodes}>{translate("Print All Qr Codes")}</Clickable>
                        </OverflowMenu>
                    </Hidden>
                </StyledWrapper>
            )}
            {addItemsToListIsOpen && (
                <StyledWrapper {...styles.addItemsGridContainer}>
                    <StyledWrapper {...styles.productSelectorGridItem}>
                        <ProductSelector
                            selectButtonTitle={translate("Add to Quote")}
                            skipInventoryValidation={true}
                            onSelectProduct={selectProductHandler}
                            productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                            productIsUnavailableMessage={siteMessage("Product_NotFound")}
                            customErrorMessage={errorMsg}
                            extendedStyles={styles.productSelectorStyles}
                        />
                        <ProductSelectorVariantModal skipInventoryValidation={true} />
                        <Link {...styles.uploadItemsLink} onClick={uploadItemsClickHandler}>
                            {translate("Upload Items")}
                        </Link>
                        <Icon
                            {...styles.closeAddItemsToList}
                            onClick={() => {
                                setAddItemsToListIsOpen(false);
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.uploadItemsGridItem}>
                        <Modal
                            {...styles.uploadItemsModal}
                            headline={translate("Upload Items To Quote")}
                            isOpen={uploadItemsModalIsOpen}
                            handleClose={uploadItemsModalCloseHandler}
                        >
                            <StyledWrapper>
                                <StyledWrapper>
                                    <OrderUpload
                                        descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                                        checkInventory={false}
                                        templateUrl="/Excel/ListUploadTemplate.xlsx"
                                        onUploadProducts={uploadProductsHandler}
                                        extendedStyles={styles.orderUploadStyles}
                                    />
                                    <OrderUploadErrorsModal
                                        descriptionText={siteMessage("ListUpload_UploadError")}
                                        uploadErrorText={siteMessage("ListUpload_UploadError")}
                                        rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                                        errorReasons={{
                                            [UploadError.ConfigurableProduct]: siteMessage(
                                                "ListUpload_CannotOrderConfigurable",
                                            ),
                                            [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                                            [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                            [UploadError.NotFound]: siteMessage("Product_NotFound"),
                                        }}
                                        extendedStyles={styles.orderUploadErrorsModalStyles}
                                    />
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Zone zoneName="UploadInstructions" contentId={widgetId} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </Modal>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <Modal
                size={400}
                isOpen={calculateMarginModalIsOpen}
                handleClose={handleCloseCalculateMarginModal}
                headline={translate("Add Price Margin")}
            >
                <TextField
                    type="text"
                    label={translate("Margin %")}
                    value={marginPercent}
                    onChange={handleMarginChange}
                    onBlur={handleMarginBlur}
                />
                <StyledWrapper className="modal-actions">
                    <Button onClick={applyMargin}>{translate("Apply")}</Button>
                    <Button variant="secondary" onClick={handleCloseCalculateMarginModal}>
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <CreateNewGroupModal
                closeModalCallback={closeModalHandler}
                callAfterSuccess={callAfterSuccess}
                productInfo={productInfo}
                product={product}
                creatingNewGroup={creatingNewGroup}
                groupingModalIsOpen={groupingModalIsOpen}
                quote={quote}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MyCustomQuoteDetailsOptions));
